/**
 * ClaimFilingTopHeader
 *
 * Combined header for the claims filing flow. Left side shows a "Back to
 * Coverage" link and large matter name. Right side shows the accident
 * description (incident note) collapsed to four lines with a Show more/less
 * toggle.
 */
import { ArrowLeft } from 'lucide-react';
import React from 'react';
import { Link, useSearchParams } from 'react-router-dom';

import { CompanionsBanner } from '@/components/matter/CompanionsBanner';
import { useGetMatterDetailsQuery } from '@/services/api/mattersService';

interface ClaimFilingTopHeaderProps {
  matterId: string;
}

export const ClaimFilingTopHeader: React.FC<ClaimFilingTopHeaderProps> = ({
  matterId,
}) => {
  const [searchParams] = useSearchParams();
  const coverageId = searchParams.get('coverageId');
  const backHref = `/matters/${matterId}/coverage${coverageId ? `?coverageId=${coverageId}` : ''}`;

  const { data: matter } = useGetMatterDetailsQuery(matterId);
  const [expanded, setExpanded] = React.useState(false);

  const incidentDescription = matter?.incident_note?.description || '';

  return (
    <div className="w-full bg-white border-b">
      <div className="mx-auto max-w-7xl px-4 py-5 flex items-start justify-between gap-8">
        <div className="min-w-0">
          <Link
            to={backHref}
            className="inline-flex items-center gap-1 text-sm text-blue-600 hover:text-blue-700"
            aria-label="Back to Coverage"
          >
            <ArrowLeft className="h-4 w-4" />
            <span>Back to Coverage</span>
          </Link>
          <h1 className="mt-3 text-4xl font-serif leading-tight text-zinc-900 m-0 truncate">
            {matter?.name || 'Matter'}
          </h1>
        </div>
        <div className="flex-1 max-w-3xl text-right mt-6">
          <div
            className={
              expanded
                ? 'text-zinc-800 text-md'
                : 'text-zinc-800 text-md line-clamp-4'
            }
          >
            {incidentDescription || '—'}
          </div>
          {incidentDescription && (
            <button
              type="button"
              onClick={() => setExpanded((v) => !v)}
              className="mt-2 text-sm text-blue-600 hover:text-blue-700"
            >
              {expanded ? 'Show less' : 'Show more'}
            </button>
          )}
        </div>
      </div>
      <div className="mx-auto max-w-7xl px-4">
        <CompanionsBanner matterId={matterId} />
      </div>
    </div>
  );
};
